import { useHead } from '@unhead/vue'

export const SITE_URL = 'https://tesla.gouyette.fr'

export type Locale = 'fr' | 'en'

/** Canonical path of each language version. French is the root (also `x-default`); `/fr` canonicalises to `/`. */
export const LOCALE_PATHS: Record<Locale, string> = {
  fr: '/',
  en: '/en'
}

export interface SEOOptions {
  /** Page title, also used for og:title */
  title: string
  /** Meta description, also used for og:description */
  description: string
  /** Language of the page: sets <html lang>, og:locale and canonical */
  lang: Locale
  ogType?: string
  twitterCard?: string
  /** Questions/answers rendered as a FAQPage JSON-LD block for rich results */
  faq?: FAQItem[]
}

export interface FAQItem {
  question: string
  answer: string
}

const OG_LOCALES: Record<Locale, string> = { fr: 'fr_FR', en: 'en_GB' }

/**
 * Declares the page head through unhead so that vite-ssg renders it in the static HTML
 * (title, description, Open Graph, canonical and hreflang alternates), not only after hydration.
 */
export function useSEO(options: SEOOptions) {
  const url = `${SITE_URL}${LOCALE_PATHS[options.lang]}`

  useHead({
    htmlAttrs: { lang: options.lang },
    title: options.title,
    meta: [
      { name: 'description', content: options.description },
      { property: 'og:title', content: options.title },
      { property: 'og:description', content: options.description },
      { property: 'og:type', content: options.ogType ?? 'website' },
      { property: 'og:url', content: url },
      { property: 'og:locale', content: OG_LOCALES[options.lang] },
      { property: 'og:site_name', content: 'Tesla Share Referral' },
      { name: 'twitter:card', content: options.twitterCard ?? 'summary' },
      { name: 'twitter:title', content: options.title },
      { name: 'twitter:description', content: options.description }
    ],
    script: options.faq?.length
      ? [
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: options.faq.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: { '@type': 'Answer', text: item.answer }
              }))
            })
          }
        ]
      : [],
    link: [
      { rel: 'canonical', href: url },
      { rel: 'alternate', hreflang: 'fr', href: `${SITE_URL}${LOCALE_PATHS.fr}` },
      { rel: 'alternate', hreflang: 'en', href: `${SITE_URL}${LOCALE_PATHS.en}` },
      { rel: 'alternate', hreflang: 'x-default', href: `${SITE_URL}/` }
    ]
  })
}
